import { PERMISSIONS } from '../../../../auth/permissions';
import type { StaysListing, StaysListingHead } from '../../../../interfaces/stays';

/**
 * ONE STAYS LISTING'S ADDRESSES (ai-plans/stays plan S4, the listing shell).
 *
 * A module of its own, NOT the shell file: S1's StaysListingDetail renders the
 * tabs and imports these, and the shell renders StaysListingDetail — defining
 * them in the shell would make the two files import each other. The shell
 * re-exports every name here, so callers read them from the shell.
 *
 * The path:
 *   /dashboard/stays/<listing uuid>                         Overview
 *   /dashboard/stays/<listing uuid>/overview                Overview (same render, no redirect)
 *   /dashboard/stays/<listing uuid>/stays                   the stays table
 *   /dashboard/stays/<listing uuid>/stays/<reservation id>  the table with that stay's edit modal open
 *   /dashboard/stays/<listing uuid>/calendar                the operations calendar
 *   /dashboard/stays/<listing uuid>/new                     the New stay page
 *   /dashboard/stays/<listing uuid>/check-in                house rules + check-in settings (plan S6)
 *   /dashboard/stays/<listing uuid>/guide[/<editor tab>]    the guest guide editor (plan S7): no tab is
 *                                                           the Guide form; access|photos|faqs|gallery|tips|preview
 *   /dashboard/stays/<listing uuid>/sync                    calendar sync: import feeds, export links (plan S5)
 *   /dashboard/stays/<listing uuid>/alerts                  the listing's alerts and message studio (plan S8)
 * Group 1 is the listing id, group 2 the section, group 3 a reservation id —
 * meaningful only with the section 'stays' (the shell ignores it elsewhere) —
 * and group 4 the guide editor's tab (only after 'guide'; a lookahead at the
 * section refuses /guide/<uuid> and /<other section>/<editor tab>, so the
 * group numbers S4 reads never move).
 * Anything else under /dashboard/stays/<uuid>/ is not matched and is a 404.
 */
export const STAYS_LISTING_PATH =
  /^\/dashboard\/stays\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/(?=(?:overview|stays|calendar|new|check-in|sync|alerts)(?:\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})?\/?$|guide(?:\/(?:access|photos|faqs|gallery|tips|preview))?\/?$)(overview|stays|calendar|new|check-in|guide|sync|alerts)(?:\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}))?(?:\/(access|photos|faqs|gallery|tips|preview))?)?\/?$/i;

export type StaysListingSection = 'overview' | 'stays' | 'calendar' | 'new' | 'check-in' | 'guide' | 'sync' | 'alerts';

/** The guide editor's tabs (plan S7.8); 'guide' is the bare /guide path. */
export type GuideEditorTab = 'guide' | 'access' | 'photos' | 'faqs' | 'gallery' | 'tips' | 'preview';

const GUIDE_TABS: ReadonlyArray<GuideEditorTab> = ['guide', 'access', 'photos', 'faqs', 'gallery', 'tips', 'preview'];

/** The guide editor tab a pathname names ('guide' for anything else). */
export const guideTabOf = (pathname: string): GuideEditorTab => {
  const m = STAYS_LISTING_PATH.exec(pathname);
  const raw = m && m[4] ? m[4].toLowerCase() : 'guide';

  return (GUIDE_TABS as ReadonlyArray<string>).includes(raw) ? (raw as GuideEditorTab) : 'guide';
};

/** The address of one guide editor tab. */
export const guideTabHref = (listingId: string, tab: GuideEditorTab): string =>
  `/dashboard/stays/${listingId}/guide${tab === 'guide' ? '' : `/${tab}`}`;

export interface StaysListingTab {
  key: string;
  label: string;
  /** The segment after /dashboard/stays/<id>; '' is the bare listing path. */
  path: string;
  permission: string;
}

/**
 * THE LISTING TABS — `STAYS_LISTING_TABS` (plan S4 OWNS this array; every
 * later plan APPENDS).
 *
 * THE APPEND CONTRACT (read before adding an entry):
 *   - Append ONE entry at the END. Never reorder, rename or remove an earlier
 *     entry — Overview is entry 0, Stays entry 1, Calendar entry 2, for good.
 *     Journeys and saved links read these labels and paths.
 *   - Every entry carries a `permission`; the shell renders a tab only when
 *     the viewer can() it, so a plan's tab never shows to a role whose every
 *     request would 403.
 *   - Add the entry's segment to the SECTION ALTERNATION of
 *     STAYS_LISTING_PATH above (`overview|stays|calendar|new|<yours>`), and
 *     render the section in StaysListingDetail's shell mode
 *     (dashboard-stays-components/stays-listing-detail.tsx).
 *   - The planned order after Calendar: Sync (S5), Check-in (S6), Guide (S7),
 *     Alerts (S8), Payments settings (S10), Registration (S11), Lock (S16).
 *   - Organisation-wide Stays sections are STAYS_SECTIONS (stays-section.tsx),
 *     not this array. 'new' is a page, not a tab, and never goes in here.
 */
export const STAYS_LISTING_TABS: StaysListingTab[] = [
  { key: 'overview', label: 'Overview', path: '', permission: PERMISSIONS.STAYS_READ },
  { key: 'stays', label: 'Stays', path: 'stays', permission: PERMISSIONS.STAYS_READ },
  { key: 'calendar', label: 'Calendar', path: 'calendar', permission: PERMISSIONS.STAYS_READ },
  // Plan S6 (S5 is deferred, so this is entry 3): house rules and check-in settings.
  { key: 'check-in', label: 'Check-in', path: 'check-in', permission: PERMISSIONS.STAYS_READ },
  // Plan S7: the guest guide editor (its own tabs live under /guide).
  { key: 'guide', label: 'Guide', path: 'guide', permission: PERMISSIONS.STAYS_READ },
  // Plan S5 (built after S6 and S7, so appended here): calendar sync — import feeds, export links.
  { key: 'sync', label: 'Calendar sync', path: 'sync', permission: PERMISSIONS.STAYS_READ },
  // Plan S8: the listing's alerts (daily summary, checkout and stay alerts, message studio, history).
  { key: 'alerts', label: 'Alerts', path: 'alerts', permission: PERMISSIONS.STAYS_READ },
];

const listingBase = (listingId: string): string => `/dashboard/stays/${listingId}`;

/** The absolute address of one section of a listing ('overview' is the bare path). */
export const listingSectionHref = (listingId: string, key: string): string => {
  const tab = STAYS_LISTING_TABS.find(t => t.key === key);
  const segment = tab ? tab.path : key === 'overview' ? '' : key;

  return segment ? `${listingBase(listingId)}/${segment}` : listingBase(listingId);
};

/** The stable deep link of one stay: the Stays tab with its edit modal open. */
export const stayHref = (listingId: string, reservationId: string): string =>
  `${listingSectionHref(listingId, 'stays')}/${reservationId}`;

/** The section a matched path names (the regex is case-insensitive). */
export const sectionOf = (raw: string | undefined | null): StaysListingSection => {
  const value = String(raw || 'overview').toLowerCase();

  return value === 'stays' || value === 'calendar' || value === 'new' || value === 'check-in' || value === 'guide'
    || value === 'sync' || value === 'alerts'
    ? value
    : 'overview';
};

/**
 * The S4 components' view of a listing, from the StaysListing S1 loads. The
 * S1 listing carries no cleaning default, so that is null here; the S4
 * responses (`listing` on the table, overview and calendar) carry the
 * server's own head when a screen needs it.
 */
export const headOf = (listing: StaysListing): StaysListingHead => ({
  id: listing.id,
  name: listing.display_name,
  status: listing.status,
  building_id: listing.building_id,
  building_name: listing.building_name,
  unit_id: listing.unit_id,
  unit_label: listing.unit_number,
  currency: listing.currency,
  max_guests: listing.max_guests,
  check_in_time: listing.check_in_time,
  check_out_time: listing.check_out_time,
  default_cleaning_fee_cents: null,
  timezone: listing.building_timezone,
  bookable: listing.status === 'active' || listing.status === 'paused',
});
